import { supabase } from '../lib/supabase';

export const categoryService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('categories')?.select('*')?.order('name', { ascending: true });
      
      if (error) throw error;
      
      return data?.map(row => ({
        id: row?.id,
        userId: row?.user_id,
        name: row?.name,
        type: row?.type,
        color: row?.color,
        icon: row?.icon,
        isPreset: row?.is_preset,
        parentId: row?.parent_id,
        emoji: row?.emoji,
        createdAt: row?.created_at
      }));
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  async getByType(type) {
    try {
      const { data, error } = await supabase?.from('categories')?.select('*')?.eq('type', type)?.order('name', { ascending: true });
      
      if (error) throw error;
      
      return data?.map(row => ({
        id: row?.id,
        userId: row?.user_id,
        name: row?.name,
        type: row?.type,
        color: row?.color,
        icon: row?.icon,
        isPreset: row?.is_preset,
        parentId: row?.parent_id,
        emoji: row?.emoji,
        createdAt: row?.created_at
      }));
    } catch (error) {
      console.error('Error fetching categories by type:', error);
      throw error;
    }
  },

  async getHierarchical() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        ?.from('categories')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('name', { ascending: true });
      
      if (error) throw error;
      
      const categories = data?.map(row => ({
        id: row?.id,
        userId: row?.user_id,
        name: row?.name,
        type: row?.type,
        color: row?.color,
        icon: row?.icon,
        isPreset: row?.is_preset,
        parentId: row?.parent_id,
        emoji: row?.emoji,
        createdAt: row?.created_at
      }));

      // Build hierarchical structure
      const parentCategories = categories?.filter(cat => !cat?.parentId);
      const childCategories = categories?.filter(cat => cat?.parentId);

      return parentCategories?.map(parent => ({
        ...parent,
        subcategories: childCategories?.filter(child => child?.parentId === parent?.id) || []
      }));
    } catch (error) {
      console.error('Error fetching hierarchical categories:', error);
      throw error;
    }
  },

  async getParentCategories(type = null) {
    try {
      let query = supabase?.from('categories')?.select('*')?.is('parent_id', null)?.order('name', { ascending: true });
      
      if (type) {
        query = query?.eq('type', type);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      
      return data?.map(row => ({
        id: row?.id,
        userId: row?.user_id,
        name: row?.name,
        type: row?.type,
        color: row?.color,
        icon: row?.icon,
        isPreset: row?.is_preset,
        emoji: row?.emoji,
        createdAt: row?.created_at
      }));
    } catch (error) {
      console.error('Error fetching parent categories:', error);
      throw error;
    }
  },

  async getSubcategories(parentId) {
    try {
      const { data, error } = await supabase?.from('categories')?.select('*')?.eq('parent_id', parentId)?.order('name', { ascending: true });
      
      if (error) throw error;
      
      return data?.map(row => ({
        id: row?.id,
        userId: row?.user_id,
        name: row?.name,
        type: row?.type,
        color: row?.color,
        icon: row?.icon,
        isPreset: row?.is_preset,
        parentId: row?.parent_id,
        createdAt: row?.created_at
      }));
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      throw error;
    }
  },

  async create(categoryData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('categories')?.insert({
          user_id: user?.id,
          name: categoryData?.name,
          type: categoryData?.type,
          color: categoryData?.color || '#6366f1',
          icon: categoryData?.icon || 'Tag',
          is_preset: false,
          parent_id: categoryData?.parentId || null,
          emoji: categoryData?.emoji || null
        })?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        userId: data?.user_id,
        name: data?.name,
        type: data?.type,
        color: data?.color,
        icon: data?.icon,
        isPreset: data?.is_preset,
        parentId: data?.parent_id,
        emoji: data?.emoji,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  },

  async update(categoryId, categoryData) {
    try {
      const updateData = {};
      
      if (categoryData?.name !== undefined) updateData.name = categoryData?.name;
      if (categoryData?.type !== undefined) updateData.type = categoryData?.type;
      if (categoryData?.color !== undefined) updateData.color = categoryData?.color;
      if (categoryData?.icon !== undefined) updateData.icon = categoryData?.icon;
      if (categoryData?.emoji !== undefined) updateData.emoji = categoryData?.emoji;
      if (categoryData?.parentId !== undefined) updateData.parent_id = categoryData?.parentId;

      const { data, error } = await supabase?.from('categories')?.update(updateData)?.eq('id', categoryId)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        userId: data?.user_id,
        name: data?.name,
        type: data?.type,
        color: data?.color,
        icon: data?.icon,
        isPreset: data?.is_preset,
        parentId: data?.parent_id,
        emoji: data?.emoji,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  },

  async delete(categoryId) {
    try {
      const { error } = await supabase?.from('categories')?.delete()?.eq('id', categoryId);
      
      if (error) throw error;
      
      return true;
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }
};